/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {FormCheck, InputGroup} from "react-bootstrap";
import PropTypes from 'prop-types';
import {findIndex, assign} from 'lodash';

import {useDispatch, useSelector} from "react-redux";
import {getManagedEvents, getManagedEventsCount, setCurrentPage, setOrder, setSelectedId} from "./managedEventsSlice";

import TextHoverDisplay from "../../common/components/TextHoverDisplay";
import TableWithPaging from '../../common/components/TableWithPaging';
import {DateTime} from "luxon";
import CheckBox from "../../common/components/CheckBox";

const ManagedEventsTable = (props) => {
    const dispatch = useDispatch();
    const managedEventsState = useSelector((state) => state.managedEvents);

    useEffect(() => {
        // When open dialog, we clear state.
        dispatch(getManagedEventsCount());
        dispatch(getManagedEvents());
    }, [props.lastReload, dispatch]);

    const createHeader = (row) => {
        return [
            {
                id: 1,
                name: '',
                property: 'temp',
                sortable: false,
                formatted: false,
            },
            {
                id: 2,
                name: 'Id',
                property: 'id',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.id.substring(0, 13)}
                            fullText={row.id}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 3,
                name: 'Domain',
                property: 'domain',
                sortable: true,
                formatted: false,
            },
            {
                id: 4,
                name: 'Type',
                property: 'typeId',
                sortable: true,
                formatted: false
            },
            {
                id: 5,
                name: 'Date Occurred',
                property: 'occurredOn',
                sortable: true,
                formatted: true,
                formatBy: (row, header) => {
                    return DateTime.fromISO(row.occurredOn).toLocaleString(DateTime.DATETIME_MED);
                }
            },
            {
                id: 6,
                name: 'Originator Type',
                property: 'originatorType',
                sortable: true,
                formatted: false
            },
            {
                id: 7,
                name: 'Originator Id',
                property: 'originatorId',
                sortable: true,
                formatted: true,
                formatBy: (row) => {
                    if (row.originatorId) {
                        return <TextHoverDisplay
                            truncatedText={row.originatorId.substring(0, 13)}
                            fullText={row.originatorId}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 8,
                name: 'Detail',
                property: 'details',
                sortable: true,
                formatted: true,
                formatBy: (row) => {
                    if (row.details) {
                        return <TextHoverDisplay
                            truncatedText={row.details.substring(0, 50)}
                            fullText={row.details}
                        />
                    }
                    return <span></span>
                }
            },
        ];
    };

    const headersUtil = (hds, hd, sortOrder) => {
        const _headers = hds.map(hc => assign(hc, {sortOrder: null}));
        _headers.splice(
            findIndex(hds, {id: hd.id}),
            1,
            assign({}, hd, {
                sortOrder: sortOrder,
            })
        );
        return _headers;
    };

    const handleSort = (header) => {
        if (!header.sortable) return;
        let _sortOrder;
        if (!header.sortOrder) _sortOrder = 'ASC';
        if (header.sortOrder === 'ASC') _sortOrder = 'DESC';
        if (header.sortOrder === 'DESC') _sortOrder = 'ASC';
        setHeader(headersUtil(headers, header, _sortOrder));
        dispatch(setOrder({by: header.property, direction: _sortOrder}));
        onSelectPage(
            1,
            {
                [header.property]: _sortOrder.toLowerCase()
            }
        );
    };

    const onSelectPage = (page) => {
        dispatch(setCurrentPage(page));
        dispatch(getManagedEvents());
    };

    const [headers, setHeader] = useState(createHeader().map(hc =>
        assign(hc, {
            sortOrder: null,
            handleSort: value => handleSort(value),
        })));

    return (
        <div>
            <TableWithPaging
                id={'source-table-with-paging'}
                headers={headers}
                rows={managedEventsState.managedEvents}
                countOfResults={managedEventsState.totalRecords}
                resultsPerPage={managedEventsState.pageSize}
                currentPage={managedEventsState.currentPage}
                onSelectPage={(page) => onSelectPage(page)}
            />
        </div>
    )
};


ManagedEventsTable.propTypes = {
    managedEvents: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
            type: PropTypes.string
        })
    ),
    handleSearch: PropTypes.func,
};

ManagedEventsTable.defaultProps = {
    style: {},
    className: '',
    labelStyle: {},
    managedEvents: []
};

export default ManagedEventsTable;
